/**
 * Canonical mentorship_meetings.status values.
 *
 * These MUST stay in sync with the mentorship_meetings_status_check CHECK
 * constraint (see supabase/migrations/*_widen_mentorship_meeting_status.sql).
 * The Zoom/invite pipeline statuses below are written by the
 * create-zoom-meeting and send-calendar-invite edge functions.
 *
 * IMPORTANT: every query that lists "active" meetings must filter on
 * SCHEDULED_MEETING_STATUSES rather than a hardcoded subset. Filtering on
 * ["scheduled", "proposed"] alone makes confirmed meetings disappear from the
 * UI as soon as the pipeline advances them past 'scheduled'.
 */

/** A meeting has been proposed but no time is confirmed yet. */
export const MEETING_STATUS_PROPOSED = "proposed";

/**
 * Statuses that represent a confirmed meeting somewhere in the
 * Zoom-creation / calendar-invite pipeline.
 */
export const SCHEDULED_MEETING_STATUSES = [
  "scheduled",
  "zoom_creating",
  "zoom_ready",
  "invites_sending",
  "invites_sent",
] as const;

/**
 * Statuses shown in the "active meetings" fetch layers: confirmed meetings
 * plus still-open proposals.
 */
export const ACTIVE_MEETING_STATUSES = [
  MEETING_STATUS_PROPOSED,
  ...SCHEDULED_MEETING_STATUSES,
] as const;

/** Terminal statuses. */
export const MEETING_STATUS_CANCELLED = "cancelled";
export const MEETING_STATUS_COMPLETED = "completed";
export const MEETING_STATUS_FAILED = "failed";

/** Every value permitted by the CHECK constraint. */
export const ALL_MEETING_STATUSES = [
  ...ACTIVE_MEETING_STATUSES,
  MEETING_STATUS_CANCELLED,
  MEETING_STATUS_COMPLETED,
  MEETING_STATUS_FAILED,
] as const;

export type MeetingStatus = (typeof ALL_MEETING_STATUSES)[number];

export function isScheduledStatus(status: string | null | undefined): boolean {
  return SCHEDULED_MEETING_STATUSES.includes(status as never);
}
